"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FrontendIModelsAccess = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const core_bentley_1 = require("@itwin/core-bentley");
const core_common_1 = require("@itwin/core-common");
const core_frontend_1 = require("@itwin/core-frontend");
const imodels_client_management_1 = require("@itwin/imodels-client-management");
const AccessTokenAdapter_1 = require("./interface-adapters/AccessTokenAdapter");
class FrontendIModelsAccess {
    constructor(iModelsClient) {
        this._emptyChangeset = { index: 0, id: "" };
        this._iModelsClient = iModelsClient !== null && iModelsClient !== void 0 ? iModelsClient : new imodels_client_management_1.IModelsClient();
    }
    async getChangesetFromId(arg) {
        const getSingleChangesetParams = {
            ...this.getIModelScopedOperationParams(arg),
            changesetId: arg.changeSetId
        };
        const changeset = await this._iModelsClient.changesets.getSingle(getSingleChangesetParams);
        if (!changeset)
            throw new core_common_1.IModelError(core_bentley_1.IModelStatus.NotFound, `Changeset ${arg.changeSetId} not found`);
        return { index: changeset.index, id: changeset.id };
    }
    async getLatestChangeset(arg) {
        const getChangesetListParams = {
            ...this.getIModelScopedOperationParams(arg),
            urlParams: {
                $top: 1,
                $orderBy: {
                    property: imodels_client_management_1.ChangesetOrderByProperty.Index,
                    operator: imodels_client_management_1.OrderByOperator.Descending
                }
            }
        };
        const changesetsIterator = this._iModelsClient.changesets.getMinimalList(getChangesetListParams);
        const changesets = await (0, imodels_client_management_1.take)(changesetsIterator, 1);
        if (!changesets.length)
            return this._emptyChangeset;
        return { index: changesets[0].index, id: changesets[0].id };
    }
    async getChangesetFromVersion(arg) {
        const version = arg.version;
        if (version.isFirst)
            return this._emptyChangeset;
        const namedVersionChangesetId = version.getAsOfChangeSet();
        if (namedVersionChangesetId)
            return this.getChangesetFromId({ ...arg, changeSetId: namedVersionChangesetId });
        const namedVersionName = version.getName();
        if (namedVersionName)
            return this.getChangesetFromNamedVersion({ ...arg, versionName: namedVersionName });
        return this.getLatestChangeset(arg);
    }
    async getChangesetFromNamedVersion(arg) {
        if (!arg.versionName)
            return this.getChangesetFromLatestNamedVersion(arg);
        const getNamedVersionListParams = {
            ...this.getIModelScopedOperationParams(arg),
            urlParams: {
                name: arg.versionName
            }
        };
        const namedVersionsIterator = this._iModelsClient.namedVersions.getMinimalList(getNamedVersionListParams);
        const namedVersions = await (0, imodels_client_management_1.take)(namedVersionsIterator, 1);
        if (namedVersions.length === 0 || !namedVersions[0].changesetId)
            throw new core_common_1.IModelError(core_bentley_1.IModelStatus.NotFound, `Named version ${arg.versionName} not found`);
        return { index: namedVersions[0].changesetIndex, id: namedVersions[0].changesetId };
    }
    getIModelScopedOperationParams(arg) {
        const authorizationCallback = arg.accessToken
            ? AccessTokenAdapter_1.AccessTokenAdapter.toAuthorizationCallback(arg.accessToken)
            : this.getAuthorizationCallbackFromIModelApp();
        return {
            authorization: authorizationCallback,
            iModelId: arg.iModelId
        };
    }
    getAuthorizationCallbackFromIModelApp() {
        return async () => {
            const token = await core_frontend_1.IModelApp.getAccessToken();
            return AccessTokenAdapter_1.AccessTokenAdapter.toAuthorization(token);
        };
    }
    async getChangesetFromLatestNamedVersion(arg) {
        const getNamedVersionListParams = {
            ...this.getIModelScopedOperationParams(arg),
            urlParams: {
                $top: 1,
                $orderBy: {
                    property: imodels_client_management_1.NamedVersionOrderByProperty.ChangesetIndex,
                    operator: imodels_client_management_1.OrderByOperator.Descending
                }
            }
        };
        const namedVersionsIterator = this._iModelsClient.namedVersions.getMinimalList(getNamedVersionListParams);
        const namedVersions = await (0, imodels_client_management_1.take)(namedVersionsIterator, 1);
        if (namedVersions.length === 0 || !namedVersions[0].changesetIndex || !namedVersions[0].changesetId)
            throw new core_common_1.IModelError(core_bentley_1.IModelStatus.NotFound, "No named versions found");
        return { index: namedVersions[0].changesetIndex, id: namedVersions[0].changesetId };
    }
}
exports.FrontendIModelsAccess = FrontendIModelsAccess;
//# sourceMappingURL=FrontendIModelsAccess.js.map